import { z } from "zod";
import { autonomyScopeSchema } from "./autonomy.js";
import { verificationContractSchema as canonicalVerificationContractSchema } from "./contract.js";
import { determinationSchema } from "./determination.js";
import { enforcementDecisionSchema, enforcementEffectSchema } from "./enforcement.js";
import { evidenceSnapshotSchema as canonicalEvidenceSnapshotSchema } from "./evidence.js";
import { actorRefSchema, serviceIdentitySchema, tenantRefSchema } from "./identity.js";
import { incidentSchema, outcomeRecordSchema } from "./outcome.js";
import { verificationReceiptV2Schema } from "./receipt.js";
import { reviewActionSchema, reviewCaseSchema, reviewDecisionSchema as canonicalReviewDecisionSchema } from "./review.js";
import { verificationRunManifestSchema as canonicalVerificationRunManifestSchema } from "./run.js";
import { workAttemptSchema as canonicalWorkAttemptSchema } from "./work-attempt.js";
import { workItemSchema as canonicalWorkItemSchema } from "./work-item.js";
import { workSnapshotSchema as canonicalWorkSnapshotSchema } from "./work-snapshot.js";
export const VERIFICATION_PRODUCT_SCHEMA_VERSION = 1;
/** Mirrors `protocol/identifiers.ts`; ranked in `verification/verdict-law.ts`. */
export const verificationStatusSchema = z.enum([
    "PASS",
    "MISMATCH",
    "FAIL",
    "UNKNOWN",
    "NOT_RUN",
    "PENDING",
]);
export const verificationReasonCodePrecedence = [
    "tenant-mismatch",
    "credential-revoked",
    "revoked-token",
    "forbidden-scope-granted",
    "over-permissioned",
    "secret-in-evidence-body",
    "fake-independence",
    "contradictory-evidence",
    "semantic-disagreement",
    "audit-failed",
    "proof-pack-rejected",
    "blocked",
    "inaccessible-evidence",
    "stale-evidence",
    "missing-evidence",
    "required-live-not-run",
    "provider-outage",
    "credential-expiring-soon",
    "policy-review-required",
    "workflow-pending-admission",
];
const verificationReasonCodeRank = new Map(verificationReasonCodePrecedence.map((reasonCode, index) => [reasonCode, index]));
export function resolveVerificationReasonCodePrecedence(reasonCodes) {
    const uniqueReasonCodes = [...new Set(reasonCodes)].filter((reasonCode) => reasonCode.length > 0);
    if (uniqueReasonCodes.length === 0) {
        throw new Error("at least one reason code is required to resolve precedence");
    }
    const winner = uniqueReasonCodes.sort((left, right) => {
        const leftRank = verificationReasonCodeRank.get(left) ?? Number.MAX_SAFE_INTEGER;
        const rightRank = verificationReasonCodeRank.get(right) ?? Number.MAX_SAFE_INTEGER;
        if (leftRank !== rightRank)
            return leftRank - rightRank;
        return left.localeCompare(right);
    })[0];
    if (!winner)
        throw new Error("at least one reason code is required to resolve precedence");
    return winner;
}
export function projectGoldenVectorWorkflowsUiOutcome(input) {
    const kind = input.kind ?? "tenant-boundary";
    const laneState = input.expectedUi?.laneState ?? input.laneState ?? "unscoped";
    const result = input.result ?? "unscoped";
    const inputs = input.inputs ?? {};
    let remediation = input.reasonCode;
    if (input.reasonCode === "blocked" || (kind === "review-case" && inputs.blocked === true)) {
        remediation = "assign-second-reviewer";
    }
    else if (input.reasonCode === "policy-review-required" ||
        (kind === "manual-review-policy" && inputs.automaticDecisionAllowed === false)) {
        remediation = "open-policy-review";
    }
    else if (input.reasonCode === "contradictory-evidence" ||
        (kind === "evidence-consistency" && inputs.sameControl === true && inputs.sameWindow === true)) {
        remediation = "inspect-conflicting-evidence";
    }
    else if (input.reasonCode === "semantic-disagreement" ||
        (kind === "semantic-worker-consensus" && inputs.requiresConsensus === true)) {
        remediation = "open-worker-trace";
    }
    else if (input.reasonCode === "credential-revoked" || input.reasonCode === "revoked-token") {
        remediation = "rotate-credential";
    }
    else if (input.reasonCode === "forbidden-scope-granted" || input.reasonCode === "over-permissioned") {
        remediation = "reduce-scopes";
    }
    else if (input.reasonCode === "credential-expiring-soon") {
        remediation = "schedule-rotation";
    }
    const reviewCaseStatus = input.status === "PASS"
        ? "closed"
        : input.status === "NOT_RUN"
            ? "awaiting-evidence"
            : remediation === "open-policy-review"
                ? "policy-review"
                : remediation === "assign-second-reviewer" ||
                    remediation === "inspect-conflicting-evidence" ||
                    remediation === "open-worker-trace"
                    ? "review-required"
                    : "remediation-required";
    const credentialHealth = input.reasonCode === "credential-revoked" || input.reasonCode === "revoked-token"
        ? "revoked"
        : input.reasonCode === "forbidden-scope-granted" || input.reasonCode === "over-permissioned"
            ? "over-scoped"
            : input.reasonCode === "credential-expiring-soon"
                ? "expiring-soon"
                : input.reasonCode === "provider-outage"
                    ? "degraded"
                    : input.status === "PASS"
                        ? "healthy"
                        : "not-applicable";
    const badgeTone = input.status === "PASS"
        ? "pass"
        // MISMATCH is red with FAIL. The tail is amber "unknown", which would
        // paint an accusation as ignorance.
        : input.status === "FAIL" || input.status === "MISMATCH"
            ? "fail"
            : input.status === "NOT_RUN"
                ? "waived"
                : "unknown";
    return {
        status: input.status,
        result,
        remediation,
        stateKey: `${kind}.${laneState}.${result}.remediation:${remediation}`,
        reviewCaseStatus,
        credentialHealth,
        uiBadge: {
            label: input.status,
            tone: badgeTone,
        },
    };
}
export const verificationAuthoritySchema = z.enum([
    "deterministic",
    "semantic",
    "human",
    "imported",
]);
export const verificationConsequenceSchema = z.enum(["low", "medium", "high", "critical"]);
export const verificationIdSchema = z.string().min(1).max(160);
export const verificationIsoTimestampSchema = z.string().datetime({ offset: true });
const versioned = {
    schemaVersion: z.literal(VERIFICATION_PRODUCT_SCHEMA_VERSION),
};
export const verificationTenantSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    name: z.string().min(1),
    createdAt: verificationIsoTimestampSchema,
});
export const verificationActorSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    role: z.enum(["owner", "operator", "control_owner", "reviewer", "auditor", "system"]),
    displayName: z.string().min(1),
});
export const verificationControlSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    framework: z.string().min(1),
    family: z.string().min(1),
    title: z.string().min(1),
    ownerActorId: verificationIdSchema.nullable(),
    consequence: verificationConsequenceSchema,
});
export const verificationRequirementSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    controlId: verificationIdSchema,
    text: z.string().min(1),
    evidenceKinds: z.array(z.string().min(1)).min(1),
});
export const verificationEvidenceArtifactSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    sourceId: verificationIdSchema,
    sourceKind: z.string().min(1),
    contentHash: z.string().min(8),
    collectedAt: verificationIsoTimestampSchema,
    collectorActorId: verificationIdSchema,
    retentionState: z.enum(["active", "redacted", "deleted", "expired"]),
});
export const verificationObservationSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    artifactId: verificationIdSchema,
    extractorVersion: z.string().min(1),
    observedAt: verificationIsoTimestampSchema,
    subjectRef: z.string().min(1),
    predicate: z.string().min(1),
    value: z.unknown(),
});
export const verificationPolicySchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    controlId: verificationIdSchema,
    version: z.string().min(1),
    activatedAt: verificationIsoTimestampSchema.nullable(),
    activatedByActorId: verificationIdSchema.nullable(),
    status: z.enum(["draft", "active", "retired"]),
    requirementIds: z.array(verificationIdSchema).min(1),
});
export const verificationCheckResultSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    runId: verificationIdSchema,
    policyId: verificationIdSchema,
    requirementId: verificationIdSchema,
    status: verificationStatusSchema,
    authority: verificationAuthoritySchema,
    observationIds: z.array(verificationIdSchema),
    evidenceArtifactIds: z.array(verificationIdSchema),
    citationRefs: z.array(z.string().min(1)),
    reason: z.string().min(1),
});
export const verificationFindingSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    runId: verificationIdSchema,
    controlId: verificationIdSchema,
    requirementId: verificationIdSchema,
    status: z.enum(["open", "accepted_exception", "remediated", "dismissed"]),
    verificationStatus: z.enum(["FAIL", "UNKNOWN"]),
    severity: verificationConsequenceSchema,
    ownerActorId: verificationIdSchema.nullable(),
    dueAt: verificationIsoTimestampSchema.nullable(),
});
export const verificationReviewDecisionSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    findingId: verificationIdSchema,
    actorId: verificationIdSchema,
    decision: z.enum(["accept_exception", "request_evidence", "approve_remediation", "reject_remediation", "dismiss"]),
    rationale: z.string().min(1),
    decidedAt: verificationIsoTimestampSchema,
    evidenceArtifactIds: z.array(verificationIdSchema),
    policyId: verificationIdSchema,
});
/**
 * WHETHER THE RECEIPT DECIDED ANYTHING — the one fact a receipt never carried.
 *
 * A receipt's `status` is the AGGREGATE of its checks, and the aggregation law
 * (`verdict-law.ts`) folds evidence-handling checks and predicate checks into
 * one symbol. That makes two materially different runs indistinguishable to
 * anyone holding only the receipt:
 *
 *   - a run whose predicate was evaluated and came back false, and
 *   - a run where nothing ever evaluated the predicate, and one evidence
 *     artifact happened to be unregistered,
 *
 * both aggregate to FAIL. Likewise a claim carrying only an opaque predicate
 * string (nothing to evaluate) and a claim bound to a DECLINED observable
 * (understood, deliberately not decided) both aggregate to UNKNOWN. And
 * `policyVersionRefs` cannot separate them either, because a NOT_RUN
 * predicate check carries the same `kind` — and therefore the same policy
 * ref — as one that decided.
 *
 * `checkResultIds` are opaque ids, not check results: the receipt names the
 * checks without saying what any of them concluded. So an external auditor
 * holding a signed receipt could not tell "I checked and it passed" from "I
 * ran no check at all". For a verification product that is the central claim
 * failing quietly, which is why this is a first-class signed field rather
 * than something a reader infers.
 *
 * It is OPTIONAL in the schema and REQUIRED at mint. Receipts already signed
 * do not carry it and cannot be retrofitted — they are append-only, signed
 * evidence. Absence therefore means exactly one thing, "this receipt does not
 * disclose", which `readReceiptDecision` reports as its own third outcome. It
 * must never be read as "undecided": that would silently re-interpret every
 * receipt issued before this field existed.
 */
export const verificationReceiptDecisionSchema = z.object({
    /**
     * TRUE iff some check in this run reached a status that DETERMINES the
     * claim — PASS, FAIL or MISMATCH — through a named evaluator. FALSE means
     * the receipt's status is a statement about evidence handling only.
     */
    decided: z.boolean(),
    /** `ref@version` of the evaluator that decided, or null when none did. */
    decidedBy: z.string().min(1).nullable(),
    /**
     * The predicate check's OWN status, unfolded from the aggregate. Null when
     * the run carried no predicate check at all — which is itself the fact an
     * auditor most needs and the aggregate most thoroughly hides.
     */
    predicateStatus: verificationStatusSchema.nullable(),
    /** Why nothing decided, in the check's own reason code. Null when decided. */
    undecidedReasonCode: z.string().min(1).nullable(),
});
export const verificationReceiptSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    runId: verificationIdSchema,
    parentReceiptId: verificationIdSchema.nullable(),
    status: verificationStatusSchema,
    decision: verificationReceiptDecisionSchema.optional(),
    checkResultIds: z.array(verificationIdSchema).min(1),
    evidenceHashes: z.array(z.string().min(8)).min(1),
    policyVersionRefs: z.array(z.string().min(1)).min(1),
    engineVersion: z.string().min(1),
    issuedAt: verificationIsoTimestampSchema,
    signature: z.object({
        keyId: z.string().min(1),
        algorithm: z.string().min(1),
        value: z.string().min(1),
    }).nullable(),
});
export const verificationRunSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    controlIds: z.array(verificationIdSchema).min(1),
    policyIds: z.array(verificationIdSchema).min(1),
    evidenceArtifactIds: z.array(verificationIdSchema),
    evidenceHashes: z.array(z.string().min(8)).min(1),
    status: verificationStatusSchema,
    startedAt: verificationIsoTimestampSchema,
    completedAt: verificationIsoTimestampSchema.nullable(),
    createdByActorId: verificationIdSchema,
    checkResultIds: z.array(verificationIdSchema).min(1),
    receiptId: verificationIdSchema.nullable(),
});
export const verificationExportPacketSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    runIds: z.array(verificationIdSchema).min(1),
    controlIds: z.array(verificationIdSchema).min(1),
    evidenceArtifactIds: z.array(verificationIdSchema),
    receiptIds: z.array(verificationIdSchema),
    redactionRefs: z.array(z.string().min(1)),
    expiresAt: verificationIsoTimestampSchema.nullable(),
    createdAt: verificationIsoTimestampSchema,
    createdByActorId: verificationIdSchema,
});
export const workItemStateSchema = z.enum(["draft", "ready", "running", "blocked", "completed", "cancelled"]);
export const workAttemptStateSchema = z.enum(["queued", "running", "succeeded", "failed", "cancelled"]);
export const verificationRunManifestStateSchema = z.enum(["planned", "running", "determined", "reviewed", "sealed"]);
export const workItemSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    state: workItemStateSchema,
    requestedOutcome: z.object({
        kind: z.enum(["determination", "enforcement", "review", "receipt", "outcome", "autonomy"]),
        targetRef: z.string().min(1),
    }),
    producer: z.object({
        actorId: verificationIdSchema,
        source: z.enum(["api", "mcp", "cli", "system", "import"]),
    }),
    environment: z.enum(["local", "staging", "production", "customer-hosted"]),
    impact: verificationConsequenceSchema,
    createdAt: verificationIsoTimestampSchema,
    updatedAt: verificationIsoTimestampSchema,
});
export const workAttemptSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    workItemId: verificationIdSchema,
    state: workAttemptStateSchema,
    startedAt: verificationIsoTimestampSchema.nullable(),
    completedAt: verificationIsoTimestampSchema.nullable(),
    errorRef: z.string().min(1).nullable(),
});
export const workSnapshotSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    workItemId: verificationIdSchema,
    treeDigest: z.string().min(8),
    contractVersion: z.string().min(1),
    capturedAt: verificationIsoTimestampSchema,
});
export const evidenceSnapshotSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    artifactIds: z.array(verificationIdSchema),
    observationIds: z.array(verificationIdSchema),
    freshnessCutoffAt: verificationIsoTimestampSchema,
    independence: z.enum(["self-attested", "same-tenant", "third-party", "cryptographic"]),
});
export const verificationContractSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    version: z.string().min(1),
    obligations: z.array(z.string().min(1)).min(1),
    policyIds: z.array(verificationIdSchema),
});
export const verificationRunManifestSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    workItemId: verificationIdSchema,
    attemptId: verificationIdSchema,
    state: verificationRunManifestStateSchema,
    contractId: verificationIdSchema,
    runId: verificationIdSchema.nullable(),
    evidenceSnapshotId: verificationIdSchema.nullable(),
    createdAt: verificationIsoTimestampSchema,
});
export const verificationErrorSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    code: z.string().min(1),
    message: z.string().min(1),
    retryable: z.boolean(),
});
export const verificationEventSchema = z.object({
    ...versioned,
    id: verificationIdSchema,
    tenantId: verificationIdSchema,
    type: z.string().min(1),
    subjectRef: z.string().min(1),
    occurredAt: verificationIsoTimestampSchema,
    payload: z.record(z.string(), z.unknown()),
});
export const verificationProductSchemas = {
    tenant: verificationTenantSchema,
    actor: verificationActorSchema,
    control: verificationControlSchema,
    requirement: verificationRequirementSchema,
    evidenceArtifact: verificationEvidenceArtifactSchema,
    observation: verificationObservationSchema,
    policy: verificationPolicySchema,
    checkResult: verificationCheckResultSchema,
    run: verificationRunSchema,
    finding: verificationFindingSchema,
    reviewDecision: verificationReviewDecisionSchema,
    receipt: verificationReceiptSchema,
    exportPacket: verificationExportPacketSchema,
    workItem: workItemSchema,
    workAttempt: workAttemptSchema,
    workSnapshot: workSnapshotSchema,
    evidenceSnapshot: evidenceSnapshotSchema,
    contract: verificationContractSchema,
    runManifest: verificationRunManifestSchema,
    error: verificationErrorSchema,
    event: verificationEventSchema,
    canonicalTenant: tenantRefSchema,
    canonicalActor: actorRefSchema,
    canonicalServiceIdentity: serviceIdentitySchema,
    canonicalWorkItem: canonicalWorkItemSchema,
    canonicalWorkAttempt: canonicalWorkAttemptSchema,
    canonicalWorkSnapshot: canonicalWorkSnapshotSchema,
    canonicalContract: canonicalVerificationContractSchema,
    canonicalEvidenceSnapshot: canonicalEvidenceSnapshotSchema,
    canonicalRunManifest: canonicalVerificationRunManifestSchema,
    determination: determinationSchema,
    enforcementDecision: enforcementDecisionSchema,
    enforcementEffect: enforcementEffectSchema,
    reviewCase: reviewCaseSchema,
    reviewAction: reviewActionSchema,
    canonicalReviewDecision: canonicalReviewDecisionSchema,
    canonicalReceipt: verificationReceiptV2Schema,
    outcome: outcomeRecordSchema,
    incident: incidentSchema,
    autonomyScope: autonomyScopeSchema,
};
/**
 * WHAT EACH REGISTRY ENTRY ACTUALLY DESCRIBES.
 *
 * The registry above is read as the product's domain model: these forty things
 * are what the system is made of. Measured 2026-09-13 that reading was wrong
 * for fourteen of them, and nothing in the registry said so — which is the same
 * defect as a catalogue naming schemas that do not exist, one layer in. A
 * published model that half-describes the product is worse than a shorter one,
 * because the reader cannot tell which half they are holding.
 *
 * Nothing is deleted. Every schema here is real, coherent and cheap to keep,
 * and several are the intended shape of work not yet wired; removing a
 * published domain model on the strength of a grep is not a call to make from
 * a scan. Instead the registry SAYS which entries describe a running object:
 *
 *   "runtime"    — some module in product source, other than the schema's own
 *                  declaring file and the unwired store below, imports this
 *                  schema or the type it infers. The running product builds
 *                  objects of this shape.
 *
 *   "store-only" — its ONLY product-source consumer is
 *                  `InMemoryVerificationProductStore` in `product-service.ts`,
 *                  which NOTHING in product source imports. Its whole caller
 *                  set in the tree is two test scripts. So the shape is
 *                  published and minted, and no running code path reaches it.
 *                  `git log --all --full-history -S InMemoryVerificationProductStore`
 *                  returns its birth commit (5a1d9d2e1, which landed the module
 *                  and its package subpath and said test scripts "land in the
 *                  next commit") plus two 2026-09-13 gate-comment commits.
 *                  There is no dropped wiring to recover: it was landed unwired,
 *                  and whether it becomes the v0 store or is retired is a
 *                  product decision, not a cleanup.
 *
 *   "unbound"    — nothing in product source binds it at all. Several of these
 *                  are decoys rather than gaps: the running incident object is
 *                  `verificationIncidentSchema` in `../incidents/index.ts`, the
 *                  running autonomy objects are `autonomyRuleSchema` /
 *                  `autonomyDecisionSchema` in `../autonomy/index.ts`, and the
 *                  running outcome objects are the `outcomeObservation*`
 *                  schemas in `../outcomes/index.ts` — none of which are these.
 *                  A reader taking `incident` here for the product's incident
 *                  gets a different object with a different field set.
 *
 * `pnpm test-verification-domain-registry-honesty` recomputes every value below
 * from source and refuses on drift in EITHER direction, so an entry that gets
 * wired must be promoted and one that loses its last consumer must be demoted.
 * Editing this map to clear that gate, rather than changing what the code binds,
 * inverts the point of it.
 */
export const verificationProductSchemaBindings = {
    tenant: "store-only",
    actor: "store-only",
    control: "store-only",
    requirement: "store-only",
    evidenceArtifact: "runtime",
    observation: "runtime",
    policy: "store-only",
    checkResult: "runtime",
    run: "runtime",
    finding: "store-only",
    reviewDecision: "unbound",
    receipt: "runtime",
    exportPacket: "runtime",
    workItem: "runtime",
    workAttempt: "runtime",
    workSnapshot: "runtime",
    evidenceSnapshot: "runtime",
    contract: "runtime",
    runManifest: "runtime",
    error: "unbound",
    event: "unbound",
    canonicalTenant: "unbound",
    canonicalActor: "runtime",
    canonicalServiceIdentity: "runtime",
    canonicalWorkItem: "runtime",
    canonicalWorkAttempt: "runtime",
    canonicalWorkSnapshot: "runtime",
    canonicalContract: "runtime",
    canonicalEvidenceSnapshot: "runtime",
    canonicalRunManifest: "runtime",
    determination: "runtime",
    enforcementDecision: "unbound",
    enforcementEffect: "unbound",
    reviewCase: "unbound",
    reviewAction: "unbound",
    canonicalReviewDecision: "unbound",
    canonicalReceipt: "runtime",
    outcome: "unbound",
    incident: "unbound",
    autonomyScope: "unbound",
};
